import React, { useEffect, useState } from "react";
import StoreLogo from "@/components/storefront/StoreLogo";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Search, ShoppingBag, X, ChevronUp, MapPin, Mail, Truck, ShieldCheck, Leaf, ArrowUpRight, Sprout } from "lucide-react";
import { toast } from "sonner";
import { Reveal } from "@/components/pharmacy/hooks";
import MobileNav from "@/components/storefront/MobileNav";
import { socialItems } from "@/lib/social";
import { hasPriceRange } from "@/lib/variants";
import { inr } from "@/lib/storeutils";
import { navLinks, footerColumns, footerLines, trustItems, isNavActive } from "@/lib/storenav";

// Look-and-feel tokens of the "Verdant Drops" layout: deep forest bands, light-green primary, lemon accent, serif headlines.
export const V = {
  deep: "var(--v-deep, #0f2e1a)", forest: "var(--v-forest, #14391f)", leaf: "var(--v-leaf, #a6e07a)", lemon: "var(--v-lemon, #f4e04d)", primary: "var(--v-primary, #84cc5c)",
  cream: "var(--v-cream, #f6fbef)", mist: "var(--v-mist, #eaf5df)", line: "var(--v-line, #dcebd0)", muted: "var(--v-muted, #5f7a62)", em: "var(--v-em, #4a9a2f)", glow: "var(--v-glow, #1d5a31)",
  serif: "'Fraunces', Georgia, serif",
};

export const pct = (p) => (p.compare_at_price ? Math.round((1 - p.price / p.compare_at_price) * 100) : 0);
export const needsOptions = (p) => p.variants?.length > 0 || p.variant_items?.length > 0;

/** Section heading: small lemon-dot eyebrow + serif title (the second part can be italic). */
export function VTitle({ eyebrow, children, light = false, center = false, className = "" }) {
  return (
    <div className={`${center ? "text-center" : ""} ${className}`}>
      {eyebrow && <div className={`inline-flex items-center gap-2 text-[12px] font-semibold ${center ? "justify-center" : ""}`} style={{ color: light ? V.leaf : V.muted }}>
        <span className="w-2 h-2 rounded-full" style={{ background: V.lemon }} />{eyebrow}</div>}
      <h2 className="mt-2 text-[28px] md:text-[36px] leading-[1.15] font-medium" style={{ fontFamily: V.serif, color: light ? "#fff" : V.deep }}>{children}</h2>
    </div>
  );
}

export function VButton({ to, onClick, children, variant = "primary", className = "", testId }) {
  const styles = variant === "lemon" ? { background: V.lemon, color: V.deep } : variant === "ghost" ? { border: "1px solid rgba(255,255,255,.4)", color: "#fff" } : { background: V.primary, color: V.deep };
  const cls = `inline-flex items-center gap-2 h-11 pl-6 pr-2 rounded-full text-[13px] font-bold transition-transform hover:-translate-y-0.5 ${className}`;
  const inner = <>{children}<span className="w-7 h-7 rounded-full flex items-center justify-center" style={{ background: V.deep, color: "#fff" }}><ArrowUpRight size={14} /></span></>;
  return to ? <Link to={to} className={cls} style={styles} data-testid={testId}>{inner}</Link> : <button type="button" onClick={onClick} className={cls} style={styles} data-testid={testId}>{inner}</button>;
}

export function VerdantHeader({ s, cart }) {
  const { store, slug } = s;
  const nav = useNavigate();
  const [searching, setSearching] = useState(false);
  const [q, setQ] = useState("");
  const [stuck, setStuck] = useState(false);
  useEffect(() => { const f = () => setStuck(window.scrollY > 40); f(); window.addEventListener("scroll", f, { passive: true }); return () => window.removeEventListener("scroll", f); }, []);
  const search = (e) => { e.preventDefault(); setSearching(false); nav(`/store/${slug}/shop${q ? `?q=${encodeURIComponent(q)}` : ""}`); };
  const { pathname } = useLocation();
  return (
    <header className="sticky top-0 z-30" style={{ background: V.deep, boxShadow: stuck ? "0 8px 28px rgba(0,0,0,.25)" : "none", transition: "box-shadow .3s" }}>
      <div className="max-w-6xl mx-auto px-5 h-[74px] flex items-center justify-between gap-6">
        <Link to={`/store/${slug}`} data-testid="storefront-name" className="shrink-0 flex items-center gap-2.5 truncate max-w-[300px]">
          <StoreLogo store={store} height={44} fallback={<>
            <span className="w-9 h-9 rounded-full flex items-center justify-center" style={{ background: V.lemon, color: V.deep }}><Leaf size={18} /></span>
            <span className="text-[20px] font-semibold text-white truncate" style={{ fontFamily: V.serif }}>{store.name}</span>
          </>} />
        </Link>
        <nav className="hidden lg:flex items-center gap-8 text-[13px] font-medium" data-testid="storefront-nav">
          {navLinks(slug).map(([l, to]) => { const on = isNavActive(pathname, slug, to); return (
            <Link key={l} to={to} aria-current={on ? "page" : undefined} className="relative py-1 transition-colors" style={{ color: on ? V.lemon : "#dbead0" }} data-testid={l === "Track Order" ? "storefront-track-link" : undefined}>
              {l}{on && <span className="absolute left-0 right-0 -bottom-0.5 h-px" style={{ background: V.lemon }} />}
            </Link>
          ); })}
        </nav>
        <div className="flex items-center gap-4 text-white">
          <button onClick={() => setSearching((x) => !x)} aria-label="Search" data-testid="verdant-search-toggle" className="hover:opacity-70"><Search size={18} /></button>
          <button onClick={() => cart.setOpen(true)} data-testid="storefront-cart-btn" aria-label="Open cart" className="relative hover:opacity-70">
            <ShoppingBag size={19} />
            <span className="absolute -top-2 -right-3 min-w-[16px] h-4 px-1 rounded-full text-[10px] font-bold flex items-center justify-center" style={{ background: V.lemon, color: V.deep }}>{cart.count}</span>
          </button>
          <Link to={`/store/${slug}/shop`} className="hidden md:inline-flex h-9 px-5 rounded-full items-center text-[12px] font-bold" style={{ background: V.lemon, color: V.deep }}>Shop now</Link>
          <MobileNav slug={slug} color="#fff" bg={V.deep} accent={V.lemon} border="rgba(255,255,255,.15)" />
        </div>
      </div>
      {searching && (
        <form onSubmit={search} className="border-t" style={{ borderColor: "rgba(255,255,255,.12)", background: V.forest }}>
          <div className="max-w-6xl mx-auto px-5 h-14 flex items-center gap-3 text-white">
            <Search size={16} style={{ color: V.leaf }} />
            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search soaps, face wash…" aria-label="Search products" data-testid="verdant-search" className="flex-1 outline-none text-[14px] bg-transparent placeholder-white/50" />
            <button type="button" onClick={() => setSearching(false)} aria-label="Close search"><X size={16} /></button>
          </div>
        </form>
      )}
    </header>
  );
}

export function VerdantFooter({ s }) {
  const { store, slug } = s;
  const [top, setTop] = useState(false);
  useEffect(() => { const f = () => setTop(window.scrollY > 600); window.addEventListener("scroll", f, { passive: true }); return () => window.removeEventListener("scroll", f); }, []);
  const lines = footerLines(store);
  const icons = { money: Sprout, verified: ShieldCheck, delivery: Truck };
  // Same structure as every theme: trust strip, store details + the three standard link columns, copyright.
  return (
    <footer data-testid="storefront-footer" style={{ background: V.deep, color: "#c9dcc0" }}>
      <div style={{ background: V.forest }}>
        <div className="max-w-6xl mx-auto px-5 py-8 grid sm:grid-cols-3 gap-6">
          {trustItems(store.requires_prescription, store.free_shipping_over).map(([key, h, d], k) => { const I = icons[key]; return (
            <Reveal key={h} delay={k * 100} className="flex gap-4 items-center">
              <span className="w-12 h-12 rounded-full flex items-center justify-center shrink-0" style={{ background: "rgba(166,224,122,.14)", color: V.lemon }}><I size={22} /></span>
              <div><div className="text-[15px] font-semibold text-white" style={{ fontFamily: V.serif }}>{h}</div><p className="text-[12px] mt-0.5" style={{ color: "#a9c4a0" }}>{d}</p></div>
            </Reveal>
          ); })}
        </div>
      </div>
      <div className="max-w-6xl mx-auto px-5 py-12 grid md:grid-cols-5 gap-9">
        <Reveal className="md:col-span-2">
          <StoreLogo store={store} height={48} fallback={<div className="flex items-center gap-2.5"><span className="w-9 h-9 rounded-full flex items-center justify-center" style={{ background: V.lemon, color: V.deep }}><Leaf size={18} /></span>
            <span className="text-[22px] font-semibold text-white" style={{ fontFamily: V.serif }}>{store.name}</span></div>} />
          <p className="text-[13px] mt-4 flex gap-3"><MapPin size={16} className="mt-0.5 shrink-0" style={{ color: V.leaf }} />{lines.location}</p>
          <p className="text-[13px] mt-2 flex gap-3"><Mail size={16} className="mt-0.5 shrink-0" style={{ color: V.leaf }} />{lines.support}</p>
          <div className="mt-4 flex gap-2.5">{socialItems(s).map((I) => <a key={I.network} href={I.url} target="_blank" rel="noopener noreferrer" aria-label={I.label} title={I.label} data-testid={`footer-social-${I.network}`}
            className="w-8 h-8 rounded-full flex items-center justify-center border transition-colors hover:bg-white/10" style={{ borderColor: "rgba(255,255,255,.25)", color: "#fff" }}><I.icon size={14} /></a>)}</div>
        </Reveal>
        {footerColumns(slug).map(([h, links], i) => (
          <Reveal key={h} delay={(i + 1) * 90}>
            <div className="text-[13px] font-bold uppercase tracking-widest text-white pb-3 border-b" style={{ borderColor: "rgba(255,255,255,.14)" }}>{h}</div>
            <ul className="mt-5 space-y-3 text-[13px]">{links.map(([l, to]) => <li key={l}><Link to={to} className="rx-link hover:text-white">{l}</Link></li>)}</ul>
          </Reveal>
        ))}
      </div>
      <div className="border-t text-center text-[12px] py-5" style={{ borderColor: "rgba(255,255,255,.12)", color: "#a9c4a0" }}>© 2026 {store.name} · Powered by <Link to="/" className="underline">Sell24x7</Link></div>
      {top && <button onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} aria-label="Back to top" className="fixed bottom-6 right-6 z-30 w-10 h-10 rounded-full flex items-center justify-center rx-slide-up" style={{ background: V.lemon, color: V.deep }}><ChevronUp size={18} /></button>}
    </footer>
  );
}

/** Product card of the Verdant layout (home shelves and the Shop page). */
export function VerdantCard({ p, s, cart, dark = false }) {
  const { slug } = s;
  const off = pct(p);
  const hasVariants = needsOptions(p);
  const soldOut = p.stock <= 0;
  const [added, setAdded] = useState(false);
  const add = (e) => {
    e.preventDefault();
    if (hasVariants) { window.location.href = `/store/${slug}/product/${p.id}`; return; }
    cart.add(p, null, 1); toast.success("Added to cart"); setAdded(true); setTimeout(() => setAdded(false), 1200);
  };
  return (
    <div data-testid={`storefront-product-${p.id}`} className="group h-full flex flex-col rounded-3xl p-3 transition-shadow hover:shadow-xl" style={{ background: dark ? V.forest : "#fff", border: `1px solid ${dark ? "rgba(255,255,255,.1)" : V.line}` }}>
      <Link to={`/store/${slug}/product/${p.id}`} className="relative block rounded-2xl overflow-hidden aspect-square" style={{ background: V.mist }}>
        {p.image ? <img src={p.image} alt={p.name} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" /> : <Leaf size={36} className="m-auto mt-[40%]" style={{ color: V.muted }} />}
        {(off > 0 || soldOut) && <span className="absolute top-2.5 left-2.5 text-[11px] font-bold px-2.5 py-1 rounded-full" style={{ background: soldOut ? "#888" : V.lemon, color: soldOut ? "#fff" : V.deep }}>{soldOut ? "Sold out" : `${off}% off`}</span>}
      </Link>
      <div className="px-1.5 pt-3 pb-1 flex flex-col flex-1">
        <Link to={`/store/${slug}/product/${p.id}`}><h3 className="text-[14px] leading-snug line-clamp-2 min-h-[2.5em]" style={{ fontFamily: V.serif, color: dark ? "#fff" : V.deep }}>{p.name}</h3></Link>
        <div className="mt-auto pt-3 flex items-center justify-between gap-2">
          <div className="flex items-baseline gap-1.5 flex-wrap">
            {hasPriceRange(p) && <span className="text-[11px]" style={{ color: V.muted }}>From</span>}
            <span className="text-[16px] font-bold" style={{ color: dark ? V.lemon : V.deep }}>{inr(p.price)}</span>
            {p.compare_at_price ? <span className="text-[12px] line-through" style={{ color: V.muted }}>{inr(p.compare_at_price)}</span> : null}
          </div>
          <button type="button" onClick={add} disabled={soldOut} data-testid={`storefront-add-to-cart-btn-${p.id}`} aria-label={hasVariants ? "Select options" : "Add to cart"}
            className="h-9 px-3.5 rounded-full text-[12px] font-bold inline-flex items-center gap-1.5 disabled:opacity-50 transition-transform hover:scale-105" style={{ background: V.primary, color: V.deep }}>
            <ShoppingBag size={13} />{added ? "Added" : hasVariants ? "Options" : "Add"}
          </button>
        </div>
      </div>
    </div>
  );
}
